import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { signKeys } from "./signs";

/**
 * Euromaster appointment integration — admin operations.
 *
 * Currently one operation: pulling the centre network from Euromaster into
 * our `sign` table (AppointmentController@syncCentres).
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface CentreMissingAddress {
  id: number;
  name: string | null;
  centre_code: string | null;
}

/**
 * Mirrors AppointmentController@syncCentres exactly.
 *
 * `status` is "partial_success" whenever at least one centre failed — the
 * endpoint then answers 207, NOT an HTTP error, so a caller that only looks
 * at the HTTP status sees a plain success and the failures vanish. Every
 * consumer of this type must read `status`, not the status code.
 */
export interface SyncCentresResult {
  status: "success" | "partial_success" | "error";
  message: string;
  total_processed: number;
  successfully_processed: number;
  centres_without_address: number;
  errors?: string[];
  centres_missing_addresses?: CentreMissingAddress[];
}

export interface SyncCentresProgress {
  state: "idle" | "running" | "finished" | "failed";
  trigger?: "admin" | "scheduler";
  total?: number;
  processed?: number;
  succeeded?: number;
  failed?: number;
  started_at?: string;
  finished_at?: string;
  updated_at?: string;
  result?: string;
  error?: string;
  /**
   * True when the run had NO concurrency protection because the server's
   * cache lock store was unavailable (usually an unwritable
   * storage/framework/cache directory). The sync still runs — that was the
   * behaviour before the lock existed — but a second run started in parallel
   * would corrupt addresses, so this must stay visible.
   */
  unlocked?: boolean;
}

// ── Constants ────────────────────────────────────────────────────────────────

/**
 * The sync is synchronous end to end: one open POST for the whole run, which
 * iterates the entire Euromaster network in batches of 50 and does per-centre
 * city/address resolution. The controller raises its own limit to 3600s.
 *
 * The shared axios instance defaults to REACT_APP_REQUEST_TIMEOUT (60s), so
 * without this override the browser aborts long before the server finishes —
 * and the server keeps going regardless. The operator would see a failure for
 * a sync that actually succeeded, and would very likely click again, running
 * a second concurrent sync over the same rows.
 *
 * 30 minutes is a client-side ceiling only. It does not stop the run.
 */
const SYNC_TIMEOUT_MS = 30 * 60 * 1000;

// ── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Live counters for a run in flight.
 *
 * A second, cheap request running alongside the blocked POST. Without it the
 * only honest thing the UI could show past the first second was elapsed time:
 * the sync request itself returns nothing until it is completely finished, so
 * a spinner alone is indistinguishable from a hung connection.
 *
 * @param enabled poll only while a run is expected to be in flight.
 */
export const useSyncCentresProgress = (enabled: boolean) => {
  const { data } = useQuery({
    queryKey: ["appointment", "sync-centres", "progress"],
    queryFn: async (): Promise<SyncCentresProgress> => {
      const res = await axios.get("/appointment/sync-centres/progress");
      return res.data ?? { state: "idle" };
    },
    enabled,
    refetchInterval: 3000,
    // Progress is disposable: a stale counter is worse than no counter.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  return data ?? null;
};

/**
 * Trigger a full Euromaster centre synchronisation.
 *
 * Writes across `sign`, `address` and `city`, and re-attaches each centre's
 * bookable services. Not idempotent in cost (it re-walks the whole network),
 * but idempotent in effect: centres are matched on `centre_code`.
 *
 * @param onSettled called after success or failure, so the caller can close
 *                  its confirmation modal in one place.
 */
export const useSyncCentres = (onSettled?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<SyncCentresResult> => {
      const response = await axios.post(
        "/appointment/sync-centres",
        {},
        { timeout: SYNC_TIMEOUT_MS }
      );
      return response.data;
    },

    onSuccess: (data) => {
      // The table, the etablissement filter and every sign detail may now be
      // stale — the sync creates and updates rows behind the UI's back.
      queryClient.invalidateQueries({ queryKey: signKeys.all });

      /*
       * A partial success gets a warning, never a green toast.
       *
       * `attachServices` in the API carries a comment about exactly this
       * failure mode: an operator who is told "done" for a run that silently
       * skipped centres has no reason to look at the report, and the missing
       * centres stay missing. The counts go in the toast so the discrepancy
       * is visible without opening anything.
       */
      if (data.status === "partial_success") {
        toast(
          t("centres.syncPartial", {
            ok: data.successfully_processed,
            total: data.total_processed,
            defaultValue: `Synchronisation terminée avec des erreurs — ${data.successfully_processed}/${data.total_processed} centres traités`,
          }),
          { icon: "⚠️", duration: 8000 }
        );
        return;
      }

      toast.success(
        t("centres.syncSuccess", {
          ok: data.successfully_processed,
          total: data.total_processed,
          defaultValue: `Synchronisation terminée — ${data.successfully_processed}/${data.total_processed} centres traités`,
        })
      );
    },

    onError: (err: any) => {
      /*
       * A client timeout is NOT a failed sync. axios reports ECONNABORTED
       * with no response; the run is still going server-side. Saying
       * "échec" here would be false, and would invite a second concurrent
       * run — the one thing an operator must not do.
       */
      if (err?.code === "ECONNABORTED" || !err?.response) {
        toast(
          t(
            "centres.syncStillRunning",
            "La réponse n'est pas revenue à temps. La synchronisation continue côté serveur — vérifiez la liste dans quelques minutes avant de relancer."
          ),
          { icon: "⏳", duration: 10000 }
        );
        return;
      }

      /*
       * 409 means the backend lock refused a SECOND run — someone else's sync,
       * or the nightly scheduler, is already working. Nothing failed and there
       * is nothing to fix, so it must not read as an error the operator should
       * act on.
       */
      if (err?.response?.status === 409) {
        toast(
          err?.response?.data?.message ||
            t(
              "centres.syncAlreadyRunning",
              "Une synchronisation est déjà en cours. Attendez qu'elle se termine."
            ),
          { icon: "⏳", duration: 8000 }
        );
        return;
      }

      /*
       * 403 is the permission gate, not a broken sync. The API puts the
       * missing permission key in the message on purpose, so an operator can
       * tell their administrator exactly what to grant.
       */
      if (err?.response?.status === 403) {
        // The axios interceptor already toasts 403s; a second toast here would
        // just stack a duplicate on screen.
        return;
      }

      toast.error(
        err?.response?.data?.message ||
          t("centres.syncError", "Échec de la synchronisation des centres")
      );
    },

    onSettled: () => {
      onSettled?.();
    },
  });
};
